import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import DatePicker from './DatePicker';
import { getStateAbbr } from '../utils/helpers';

const AddRegisteredAgentDialog = ({ open, onClose, state, onSave, existingData }) => {
  const [formData, setFormData] = useState({
    agentName: '',
    agentType: '',
    address: '',
    city: '',
    agentState: '',
    zip: '',
    phone: '',
    email: '',
    appointedDate: '',
    fee: ''
  });

  useEffect(() => {
    if (existingData) {
      setFormData(existingData);
    } else {
      setFormData({
        agentName: '',
        agentType: '',
        address: '',
        city: '',
        agentState: getStateAbbr(state) || '', // Pre-select state abbreviation from the card
        zip: '',
        phone: '',
        email: '',
        appointedDate: '',
        fee: ''
      });
    }
  }, [existingData, open, state]);

  const handleDateChange = (value) => {
    // Format as MM/DD/YYYY while typing
    let cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      cleaned = cleaned.slice(0, 2) + '/' + cleaned.slice(2);
    }
    if (cleaned.length >= 5) {
      cleaned = cleaned.slice(0, 5) + '/' + cleaned.slice(5, 9);
    }
    setFormData({ ...formData, appointedDate: cleaned });
  };

  const handlePhoneChange = (value) => {
    // Format as (000) 000-0000 while typing
    let cleaned = value.replace(/\D/g, '');
    let formatted = cleaned;
    
    if (cleaned.length >= 1) {
      formatted = '(' + cleaned.slice(0, 3);
    }
    if (cleaned.length >= 4) {
      formatted = '(' + cleaned.slice(0, 3) + ') ' + cleaned.slice(3, 6);
    }
    if (cleaned.length >= 7) {
      formatted = '(' + cleaned.slice(0, 3) + ') ' + cleaned.slice(3, 6) + '-' + cleaned.slice(6, 10);
    }
    
    setFormData({ ...formData, phone: formatted });
  };

  const isValidEmail = (email) => {
    if (!email) return true; // Empty is valid (optional field)
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSave = () => {
    // Validate email format if provided
    if (formData.email && !isValidEmail(formData.email)) {
      alert('Please enter a valid email address (e.g., name@example.com)');
      return;
    }
    
    onSave(formData);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-registered-agent-dialog">
        <DialogHeader>
          <DialogTitle>{existingData?.agentName ? 'Edit' : 'Add'} Registered Agent - {state}</DialogTitle>
          <DialogDescription>
            Enter the registered agent information for {state}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="agent-name">Agent Name*</Label>
              <Input
                id="agent-name"
                value={formData.agentName}
                onChange={(e) => setFormData({ ...formData, agentName: e.target.value })}
                placeholder="Enter agent name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="agent-type">Agent Type</Label>
              <Input
                id="agent-type"
                value={formData.agentType}
                onChange={(e) => setFormData({ ...formData, agentType: e.target.value })}
                placeholder="e.g., Commercial Service"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Street Address</Label>
            <Input
              id="address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="Enter street address"
            />
          </div>

          <div className="grid grid-cols-4 gap-3">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder="City"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="agent-state">State</Label>
              <Input
                id="agent-state"
                value={formData.agentState}
                onChange={(e) => setFormData({ ...formData, agentState: e.target.value.toUpperCase() })}
                placeholder="ST"
                maxLength="2"
                className="uppercase"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="zip">ZIP</Label>
              <Input
                id="zip"
                value={formData.zip}
                onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                placeholder="ZIP"
                maxLength="10"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                placeholder="(000) 000-0000"
                maxLength="14"
              />
              <p className="text-xs text-muted-foreground">Format: (000) 000-0000</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="agent@example.com"
                className={formData.email && !isValidEmail(formData.email) ? 'border-red-500' : ''}
              />
              {formData.email && !isValidEmail(formData.email) && (
                <p className="text-xs text-red-500">Please enter a valid email address</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="appointed-date">Appointment Date</Label>
              <DatePicker
                id="appointed-date"
                value={formData.appointedDate}
                onChange={(value) => setFormData({ ...formData, appointedDate: value })}
                placeholder="MM/DD/YYYY"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fee">Annual Fee</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="fee"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={formData.fee}
                  onChange={(e) => setFormData({ ...formData, fee: e.target.value })}
                  className="pl-7"
                />
              </div>
              <p className="text-xs text-muted-foreground">Annual service fee</p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={!formData.agentName}>
            {existingData?.agentName ? 'Update' : 'Add'} Registered Agent
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddRegisteredAgentDialog;
